import { useEffect, useRef, useState } from "react";
import { translate as tr } from "../../translations";
import "./connectionState.css";

const OFFLINE_STATUSES = new Set([0, 408, "FETCH_ERROR", "TIMEOUT_ERROR", "PARSING_ERROR"]);

/**
 * Hatanın türü: "maintenance" (503 MAINTENANCE), "offline" (yanıt yok, 0,
 * 408, zaman aşımı) ya da "error" (diğer her şey, hata nesnesi yoksa da).
 */
export function connectionKind(error) {
  if (!error) return "error";
  const type = error.type || error.errorType || error.data?.errorType;
  if (type === "MAINTENANCE") return "maintenance";
  if (error.status === undefined || error.status === null || OFFLINE_STATUSES.has(error.status)) return "offline";
  return "error";
}

const COPY = {
  offline: { title: "cs_offline_title", body: "cs_offline_body" },
  maintenance: { title: "cs_maintenance_title", body: "cs_maintenance_body" },
  error: { title: "cs_error_title", body: "cs_error_body" },
};

function WifiArt() {
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%" aria-hidden>
      <g fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="4">
        <path className="tk-cs-arc tk-cs-arc-3" d="M8 26 a34 34 0 0 1 48 0" />
        <path className="tk-cs-arc tk-cs-arc-2" d="M16 34 a23 23 0 0 1 32 0" />
        <path className="tk-cs-arc tk-cs-arc-1" d="M24 42 a12 12 0 0 1 16 0" />
      </g>
      <circle cx="32" cy="50" r="3.5" fill="currentColor" />
    </svg>
  );
}

function Gear({ cx, cy, r, teeth, className }) {
  const toothW = r * 0.42;
  const toothH = r * 0.38;
  return (
    <g className={`tk-cs-gear ${className}`}>
      {Array.from({ length: teeth }, (_, i) => (
        <rect
          key={i}
          x={cx - toothW / 2}
          y={cy - r - toothH + 1}
          width={toothW}
          height={toothH}
          rx={1}
          fill="currentColor"
          transform={`rotate(${(360 / teeth) * i} ${cx} ${cy})`}
        />
      ))}
      <circle cx={cx} cy={cy} r={r} fill="currentColor" />
      <circle cx={cx} cy={cy} r={r * 0.4} className="tk-cs-hole" fill="var(--tk-cs-hole, #fff)" />
    </g>
  );
}

function GearsArt() {
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%" aria-hidden>
      <Gear cx={24} cy={28} r={13} teeth={9} className="tk-cs-gear-a" />
      <Gear cx={45} cy={44} r={8.5} teeth={7} className="tk-cs-gear-b" />
    </svg>
  );
}

function CloudArt() {
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%" aria-hidden>
      <g className="tk-cs-cloud">
        <path
          d="M18 46 h30 a10 10 0 0 0 1-20 a14 14 0 0 0-27-3 a11 11 0 0 0-4 23 z"
          fill="currentColor"
          opacity="0.22"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        <path d="M33 26 v9" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
        <circle cx="33" cy="41" r="2.2" fill="currentColor" />
      </g>
    </svg>
  );
}

const ART = { offline: WifiArt, maintenance: GearsArt, error: CloudArt };

/**
 * Veri ekranlarının ortak hata durumu (mobil ConnectionState): bağlantı
 * yoksa sırayla yanan Wi-Fi yayları, bakımda ters dönen iki dişli, diğer
 * hatalarda hafifçe sallanan ünlemli bulut. message verilirse "Bir şeyler
 * ters gitti" başlığının yerine geçer (yalnız genel hatada). onRetry bir
 * söz (promise) döndürürse ya da retrying verilirse düğme "Deneniyor" olur.
 */
export default function ConnectionState({ error, onRetry, retrying = false, message, compact = false, className = "" }) {
  const kind = connectionKind(error);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  const Art = ART[kind];
  const title = kind === "error" && message ? message : tr(COPY[kind].title);
  const isRetrying = retrying || busy;

  const handleRetry = async () => {
    if (!onRetry || isRetrying) return;
    const result = onRetry();
    if (result && typeof result.then === "function") {
      setBusy(true);
      try {
        await result;
      } catch {
        // Hata durumu sorgudan gelir.
      } finally {
        if (mounted.current) setBusy(false);
      }
    }
  };

  return (
    <div
      role="alert"
      data-connection-state={kind}
      className={`flex flex-col items-center text-center gap-2 ${compact ? "py-6 px-4" : "py-10 px-6"} ${className}`}
    >
      <span
        className={`${compact ? "w-12 h-12" : "w-16 h-16"} text-terakkiBrand dark:text-terakkiBrandSoft [--tk-cs-hole:#ffffff] dark:[--tk-cs-hole:#0F3436]`}
      >
        <Art />
      </span>
      <p className="text-[15px] font-medium text-terakkiInk dark:text-white">{title}</p>
      <p className="max-w-xs text-sm leading-5 text-terakkiNeutral dark:text-white/70">{tr(COPY[kind].body)}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={handleRetry}
          disabled={isRetrying}
          className="mt-1 h-9 px-4 rounded-full text-sm font-medium bg-terakkiBrandCloud text-terakkiPrimaryDark hover:bg-terakkiBrandMist transition-colors disabled:opacity-60 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
        >
          {tr(isRetrying ? "cs_retrying" : "cs_retry")}
        </button>
      ) : null}
    </div>
  );
}
